/**
 * THE ENTRY EVERY VISITOR EXECUTES — website and CAD alike.
 *
 * ⛔ THIS FILE MUST NOT STATICALLY REACH THE CAD ENGINE (WR-486). Nothing here, and
 * nothing transitively imported from here, may import `three`, `src/core/state/store`,
 * or `src/core/rendering`. Those belong to `src/cad/cadBoot.ts`, which is reached only
 * from the `lazy()` callback in `AppRouter.tsx` when `/app` is entered.
 *
 * Measured before the split (HEAD 05cb02b64): this entry's static closure was 429 files
 * / 4.5 MB, the emitted entry chunk 1,951,527 B raw (429,852 B brotli), and every one of
 * the 12 website routes fetched `three`, `src/core/rendering` and the store before it
 * painted. Enforced by `src/__tests__/architecture/websiteEntryExcludesCadEngine.test.ts`;
 * the built-artifact number is re-measured by
 * `architecture/Website/entry-split-s1/measure-entry.mjs`.
 *
 * WHAT DELIBERATELY STAYS HERE, AND WHY — each of these was checked, not assumed:
 *
 *  · `loadFontsForCanvas()` — it is NOT only "for the Carota editor". It injects the
 *    `@font-face` rules for **Inter 700**, which the website uses in four places
 *    (`LoginPage.module.css:128`, `AuthConfirmPage.module.css:63`,
 *    `LandingPage.module.css:415` and `:568`). `index.html:12` asks Google Fonts for
 *    Inter 400;500;600 only, and `src/index.css:6` sets `font-synthesis: none`, so there
 *    is no synthesised fallback: moving this call visibly lightens every website heading.
 *    Its closure is one file and no packages, so it costs the entry nothing.
 *
 *  · The PROD-guarded `inject()` and its comment block — `vercelAnalyticsProdOnly.test.ts`
 *    asserts this file is the ONLY non-test caller, and it strips comments before matching
 *    *because this docblock contains the words `inject()`*. WR-484 exists to attribute
 *    LANDING-PAGE traffic, so the call must stay on the path a website visitor actually
 *    executes — not merely in a file named `main.tsx`.
 *
 *  · The IndexedDB gate and `bootstrapStorageFoundation()`, in their existing order —
 *    `storageFoundationBootWiring.matrix.test.ts` GATE-2 is a SOURCE-SHAPE pin asserting
 *    `isIndexedDbSupported()` → `renderUnsupportedBrowserMessage(` →
 *    `bootstrapStorageFoundation()` → `ReactDOM.createRoot(` in that order, and mutation
 *    M-F proved it load-bearing. Deferring them would also change what an IndexedDB-less
 *    visitor sees on the marketing page, which is a product decision nobody has taken
 *    (registered as WR-488).
 *
 * @see architecture/Website/entry-split-s1/stage4-proposal-v4.md §1.4
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { inject } from "@vercel/analytics";
import "./index.css";

import { AppRouter } from "./AppRouter";
import { loadFontsForCanvas } from "./core/text/services/fontLoadingService";
import {
  isIndexedDbSupported,
  renderUnsupportedBrowserMessage,
} from "@/core/storage/requireIndexedDB";
import { bootstrapStorageFoundation } from "@/core/storage/storageFoundationBoot";

// Load web fonts. Named "forCanvas" because Carota (the CAD text editor) needs the
// Fontsource files, but the same injected @font-face rules are what give the WEBSITE its
// Inter 700 — see the header. Fire-and-forget, `font-display: swap`, idempotent.
loadFontsForCanvas();

// Initialize Supabase auth early (restore session, detect magic link callback)
import("./core/services/supabaseAuthService").then(
  ({ supabaseAuthService }) => {
    supabaseAuthService.initialize().catch((error) => {
      console.error("Failed to initialize auth service:", error);
    });
  },
);

// Vercel Web Analytics.
//
// PRODUCTION ONLY, deliberately. Vercel Web Analytics was enabled on the project
// 2026-09-11 but the Vite app ships no collection script, so no data flows until
// inject() runs. Guarding on import.meta.env.PROD keeps dev servers and the test
// suite from emitting page-view beacons — local navigation is not product data,
// and a test run must never phone home.
//
// MEASURED (auth-confirm-s1, 2026-09-11), because the mechanism differs by mode:
//   - dev server: the branch is NOT stripped — `curl localhost:<port>/src/main.tsx`
//     still shows it — it is simply evaluated at runtime, where PROD is false.
//     A headless load of a dev page made 689 requests and ZERO analytics beacons.
//   - production build: `npm run build` emits the beacon endpoint into
//     dist/assets/index-*.js, so inject() is genuinely live once deployed.
//   - vitest: import.meta.env.PROD is false (MODE="test"), and no test imports
//     this file at all.
if (import.meta.env.PROD) {
  inject();
}

// beforeunload handling is consolidated in PersistenceGuard (initialized in App.tsx)

// Initialize WebSocket Console Logger (Phase 3 - Enhanced Reliability)
if (import.meta.env.DEV) {
  import("./core/monitoring/websocketLogger")
    .then(({ WebSocketLogger }) => {
      try {
        new WebSocketLogger();
        console.log("✅ WebSocket Console Logger initialized");
      } catch (error) {
        console.error("❌ Failed to initialize WebSocket logger:", error);
      }
    })
    .catch((error) => {
      console.error("❌ Failed to load WebSocket logger module:", error);
    });
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Fatal: Root element #root not found in document");
}

// Require-IndexedDB startup gate (Bombproof storage foundation, step 6 —
// approval-gate §7): IndexedDB is the sole document store; without it the app
// shows a clear unsupported-browser message instead of a degraded document
// store. The storage-foundation bootstrap (one-time legacy LS→IDB document
// migration FIRST, then the orphan sweep — NA-B2″ order) runs at this
// imperative chokepoint, never from a React effect.
if (!isIndexedDbSupported()) {
  renderUnsupportedBrowserMessage(rootElement);
} else {
  bootstrapStorageFoundation().catch((error) => {
    console.error("Storage foundation bootstrap failed:", error);
  });

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <AppRouter />
    </React.StrictMode>,
  );
}
